import { __ } from '@/hub-shared/utils/i18n';
import {
  allowGate,
  getAdvancedUserManagementGate,
  permissionsSubscriptionRequiredGate,
  type FeatureGateDecision,
  type FeatureGateReason,
} from '@/hub-shared/gates/featureGateDecisions';
import type { PortalBootstrapData } from '../types/window';

export type PortalGateKey =
  | 'cabinet.roles.advanced_permissions'
  | 'cabinet.compliance.dossier_attribution'
  | 'cabinet.compliance.mission_policy'
  | 'vsme.access';

export type PortalGateReason = FeatureGateReason;

export type PortalGateDecision = FeatureGateDecision;

/**
 * Role template editor: subscription first, then users.manage_access for saving / editing dropdowns.
 */
export const getCabinetRolesTemplateEditGate = (
  bootstrap: PortalBootstrapData,
): PortalGateDecision => {
  const gate = getAdvancedUserManagementGate({
    advancedUserManagement: bootstrap.capabilities.advancedUserManagement === true,
    subscriptionTierUrl: bootstrap.subscriptionTierUrl,
    manageUserAccess: bootstrap.capabilities.manageUserAccess === true,
    requireManageUserAccess: true,
  });
  if (!gate.allowed && gate.reason === 'subscription_required') {
    return permissionsSubscriptionRequiredGate(bootstrap.subscriptionTierUrl);
  }
  return gate;
};

const canSeeCabinetRolesTab = (bootstrap: PortalBootstrapData): boolean => {
  if (bootstrap.entitlements?.planId === 'memorialist') {
    return false;
  }

  return bootstrap.capabilities.manageUserAccess === true;
};

export const CABINET_TAB_IDS = ['info', 'themes', 'compliance', 'files', 'subscription', 'roles'] as const;

export type CabinetTabId = (typeof CABINET_TAB_IDS)[number];

const canSeeCabinetSubscriptionTab = (bootstrap: PortalBootstrapData): boolean => {
  if (bootstrap.entitlements?.planId === 'memorialist') {
    return false;
  }

  return bootstrap.capabilities.manageBilling === true;
};

/**
 * Cabinet SPA tabs visible for the current actor (files = `GET /cabinet` names a self store the
 * actor may read; subscription = billing.manage; permissions = role-template admin).
 */
export const getVisibleCabinetTabIds = (
  bootstrap: PortalBootstrapData,
  selfFileStoreOwnerId: number | null = null,
): readonly CabinetTabId[] => {
  const tabs: CabinetTabId[] = ['info', 'themes', 'compliance'];
  if (selfFileStoreOwnerId !== null && selfFileStoreOwnerId > 0) {
    tabs.push('files');
  }
  if (canSeeCabinetSubscriptionTab(bootstrap)) {
    tabs.push('subscription');
  }
  if (canSeeCabinetRolesTab(bootstrap)) {
    tabs.push('roles');
  }
  return tabs;
};

type CabinetProfileEditGateOptions = {
  /** From GET/PATCH `cabinet` when available (includes subscription). */
  canEditCabinet?: boolean;
  /** Bootstrap fallback before cabinet payload loads. */
  isCabinetOwner?: boolean;
};

/** Cabinet logo, identity fields, and cabinet-level report theme (`perm.cabinet.edit`; no upgrade CTA). */
export const getCabinetProfileEditGate = (
  options: CabinetProfileEditGateOptions,
): PortalGateDecision => {
  const allowed =
    options.canEditCabinet !== undefined
      ? options.canEditCabinet
      : options.isCabinetOwner === true;

  if (allowed) {
    return allowGate();
  }

  return {
    allowed: false,
    reason: 'missing_permission',
    message: __('Vous n’avez pas la permission de modifier les paramètres du cabinet.'),
  };
};

export const getConnectionsManageGate = (
  bootstrap: PortalBootstrapData,
): PortalGateDecision => {
  if (bootstrap.capabilities.manageConnections === true) {
    return allowGate();
  }

  return {
    allowed: false,
    reason: 'missing_permission',
    message: __('Vous n’avez pas la permission de gérer les connexions API aux logiciels tiers.'),
  };
};

export const getCabinetManageControlLevelGate = (
  bootstrap: PortalBootstrapData,
): PortalGateDecision => {
  if (bootstrap.capabilities.cabinetManageControlLevel === true) {
    return allowGate();
  }

  return {
    allowed: false,
    reason: 'missing_permission',
    message: __('Vous n’avez pas la permission de gérer le contrôle et la conformité.'),
  };
};

export const getPortalGate = (
  bootstrap: PortalBootstrapData,
  gateKey: PortalGateKey,
): PortalGateDecision => {
  if (gateKey === 'cabinet.roles.advanced_permissions') {
    return bootstrap.capabilities.advancedUserManagement === true
      ? allowGate()
      : permissionsSubscriptionRequiredGate(bootstrap.subscriptionTierUrl);
  }

  if (
    gateKey === 'cabinet.compliance.dossier_attribution'
    || gateKey === 'cabinet.compliance.mission_policy'
  ) {
    return getAdvancedUserManagementGate({
      advancedUserManagement: bootstrap.capabilities.advancedUserManagement === true,
      subscriptionTierUrl: bootstrap.subscriptionTierUrl,
    });
  }

  if (gateKey === 'vsme.access') {
    return bootstrap.capabilities.manageVsme ? allowGate() : {
      allowed: false,
      reason: 'missing_permission',
      message: __('Vous ne pouvez pas accéder à cette fonctionnalité.'),
    };
  }

  return {
    allowed: false,
    reason: 'feature_disabled',
    message: __('Cette fonctionnalité est indisponible.'),
  };
};
